import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Row,
  Spinner,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFileAlt,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { fetchApi } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import CustomTable from "../../components/CustomTable";
import AuditStatusBadge from "../../components/audit/AuditStatusBadge";

const STAGE_LABEL = {
  1: "Stage 1 · Auditor General",
  2: "Stage 2 · Release auditor",
};

const EMPTY_FILTERS = {
  mda: "",
  dateFrom: "",
  dateTo: "",
  stage: "",
  category: "",
  outcome: "",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Audit decisions for management.
 *
 * One row per release: the decision recorded at stage 1 or stage 2, who
 * recorded it, and the exceptions raised against it. The filters narrow the
 * same list, and the Excel export writes exactly what is on screen.
 */
const AuditReport = () => {
  const [rows, setRows] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const load = useCallback(() => {
    setLoading(true);
    setError("");

    const params = new URLSearchParams();
    if (applied.mda) params.set("mda", applied.mda);
    if (applied.dateFrom) params.set("date_from", applied.dateFrom);
    if (applied.dateTo) params.set("date_to", applied.dateTo);
    if (applied.stage) params.set("stage", applied.stage);
    if (applied.category) params.set("category", applied.category);
    if (applied.outcome) params.set("outcome", applied.outcome);

    fetchApi(`audit/report?${params.toString()}`)
      .then((response) => {
        if (response?.success) {
          setRows(response.data || []);
          setTotalAmount(Number(response.total_amount) || 0);
          if (response.categories?.length) setCategories(response.categories);
        } else {
          setRows([]);
          setTotalAmount(0);
          setError(response?.message || "Could not load the audit report.");
        }
      })
      .catch(() => {
        setRows([]);
        setTotalAmount(0);
        setError("Could not load the audit report. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [applied]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (name) => (event) =>
    setFilters((current) => ({ ...current, [name]: event.target.value }));

  const submit = (event) => {
    event.preventDefault();
    setApplied({ ...filters, mda: filters.mda.trim() });
  };

  const clear = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const approvedCount = rows.filter((row) =>
    ["AG Approved", "Audit Approved"].includes(row.audit_status)
  ).length;
  const flaggedCount = rows.filter((row) =>
    ["AG Flagged", "Audit Flagged"].includes(row.audit_status)
  ).length;

  const exportToExcel = () => {
    if (!rows.length) return;

    const sheetRows = rows.map((row, index) => ({
      "S/N": index + 1,
      "Approval No": row.approval_no,
      MDA: row.mda_name || "",
      "MDA Code": row.mda_code || "",
      Type: row.approval_type || "",
      Amount: Number(row.amount) || 0,
      "Collection Date": row.collection_date || "",
      "Audit Status": row.audit_status || "",
      Stage: STAGE_LABEL[Number(row.audit_stage)] || "",
      "Decision By": row.auditor_name || "",
      "Decision Date": row.audit_at ? formatDate(row.audit_at) : "",
      Exceptions: Number(row.exception_count) || 0,
      "Exception Categories": row.exception_categories || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Report");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");

    FileSaver.saveAs(
      new Blob([buffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      `Audit_Report_${stamp}.xlsx`
    );
  };

  const fields = [
    { title: "Approval No", value: "approval_no" },
    { title: "MDA", value: "mda_name" },
    { title: "Type", value: "approval_type" },
    {
      title: "Amount",
      custom: true,
      component: (item) => <div className="text-end">{formatNumber(item.amount)}</div>,
    },
    { title: "Collection Date", value: "collection_date" },
    {
      title: "Audit Status",
      custom: true,
      component: (item) => <AuditStatusBadge status={item.audit_status} />,
    },
    {
      title: "Stage",
      custom: true,
      component: (item) => (
        <small className="text-muted">{STAGE_LABEL[Number(item.audit_stage)] || "—"}</small>
      ),
    },
    {
      title: "Decision By",
      custom: true,
      component: (item) => item.auditor_name || <span className="text-muted">—</span>,
    },
    {
      title: "Decision Date",
      custom: true,
      component: (item) => formatDate(item.audit_at),
    },
    {
      title: "Exceptions",
      custom: true,
      component: (item) =>
        Number(item.exception_count) > 0 ? (
          <div>
            <Badge bg="danger">{item.exception_count}</Badge>
            {item.exception_categories && (
              <div className="small text-muted">{item.exception_categories}</div>
            )}
          </div>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-3 mb-md-0">
          <h4 className="mb-1">
            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
            Audit Report
          </h4>
          <p className="text-muted mb-0">
            Every recorded audit decision, with the exceptions raised against it.
            {rows.length > 0 &&
              ` ${rows.length} decision${rows.length === 1 ? "" : "s"} · ${formatNumber(
                totalAmount
              )} · ${approvedCount} approved · ${flaggedCount} flagged`}
            {rows.length >= 500 &&
              " — showing the first 500; narrow the filters to export a complete list."}
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="outline-primary" size="sm" onClick={load} disabled={loading}>
            <FontAwesomeIcon icon={faSync} className={`me-1 ${loading ? "fa-spin" : ""}`} />
            Refresh
          </Button>
          <Dropdown>
            <Dropdown.Toggle variant="outline-success" size="sm" disabled={!rows.length}>
              <FontAwesomeIcon icon={faDownload} className="me-1" />
              Export
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={exportToExcel}>Excel (.xlsx)</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={submit}>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="small text-muted">MDA</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Filter by MDA name..."
                    value={filters.mda}
                    onChange={update("mda")}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <Form.Label className="small text-muted">Collected from</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.dateFrom}
                    onChange={update("dateFrom")}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <Form.Label className="small text-muted">Collected to</Form.Label>
                  <Form.Control type="date" value={filters.dateTo} onChange={update("dateTo")} />
                </Form.Group>
              </Col>
              <Col xs={12} md={2}>
                <Form.Group>
                  <Form.Label className="small text-muted">Stage</Form.Label>
                  <Form.Select value={filters.stage} onChange={update("stage")}>
                    <option value="">All stages</option>
                    <option value="1">Stage 1 · Auditor General</option>
                    <option value="2">Stage 2 · Release auditor</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="small text-muted">Exception category</Form.Label>
                  <Form.Select value={filters.category} onChange={update("category")}>
                    <option value="">All categories</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-2 align-items-end mt-1">
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="small text-muted">Outcome</Form.Label>
                  <Form.Select value={filters.outcome} onChange={update("outcome")}>
                    <option value="">All decisions</option>
                    <option value="approved">Approved only</option>
                    <option value="flagged">Flagged only</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Button type="submit" variant="primary" className="w-100">
                  Apply
                </Button>
              </Col>
              <Col xs={6} md={2}>
                <Button variant="outline-secondary" className="w-100" onClick={clear}>
                  Clear
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card border="light" className="table-wrapper shadow-sm">
        <Card.Body className="p-0">
          {error ? (
            <div className="alert alert-danger m-3 mb-0">{error}</div>
          ) : loading && !rows.length ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 mb-0 text-muted">Loading audit report…</p>
            </div>
          ) : (
            <CustomTable fields={fields} data={rows} />
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default AuditReport;
